/* eslint-disable no-nested-ternary */
import { PropsWithChildren } from "react";
import { sideColors } from "../../../utils/sideColors";
import { INDEX_PRICE_COLLAR_OPTIONS } from "../../../constants/precision/form";
import { AccountStateEnum } from "../../../contexts/AuthContext";
import ClosePositionModal from "../../ConfirmationModal/ClosePositionModal";
import OptionFilter from "../../OptionFilter";
import TicketDetails from "../../TicketDetails";
import SegmentedControl from "../../shared/SegmentedControl";
import { Spinner } from "../../shared/Spinner";
import {
  ContentWrapper,
  DetailsWrapper,
  FormContent,
  OrderTypeWrapper,
  Padding,
  PlaceOrderButton,
  SubmitWrapper,
  TradeExecutedBar,
  TradeForm,
  TransactionTypeWrapper,
} from "../style";
import { ITradeModalProps } from "./types";
import { InfoSection } from "./InfoSection";
import { InputsSection } from "./InputsSection";
import { useOptionsTradeFormSetup } from "./useOptionsTradeFormSetup";
import { useOptionsTradeFormComputed } from "./useOptionsTradeFormComputed";
import { useOptionsTradeFormPosition } from "./useOptionsTradeFormPosition";
import { useOptionsTradeFormSubmit } from "./useOptionsTradeFormSubmit";
import { useOptionsTradeFormHandlers } from "./useOptionsTradeFormHandlers";

function OptionsTradeForm({
  selectedInstrument,
  mobileMode,
  onClose,
}: PropsWithChildren<ITradeModalProps>) {
  const setup = useOptionsTradeFormSetup({ selectedInstrument });
  const computed = useOptionsTradeFormComputed(setup);
  const position = useOptionsTradeFormPosition({ ...setup, ...computed });
  const { submitOrder } = useOptionsTradeFormSubmit({
    ...setup,
    ...computed,
    ...position,
    onClose,
  });
  const handlers = useOptionsTradeFormHandlers({
    ...setup,
    ...computed,
    ...position,
  });

  const sectionProps = { ...setup, ...computed, ...position, ...handlers };

  const {
    market,
    commonFormTranslations,
    optionsTradeFormTranslations,
    accountSigningKeyState,
    orderbookData,
    index,
    animControls,
    reduceOnly,
    orderSide,
    orderType,
    isLoading,
    showCloseModal,
    modalRef,
    isSticky,
    errors,
    handleSubmit,
    amount,
    orderData,
    accountData,
    showOnboarding,
    ticker,
    buySellSegmentProps,
    limitMarketSegmentProps,
    currentPosition,
    totalValue,
    reduceOnlyDisabled,
    reduceOnlyNotAllowed,
    onClosePositionModalHide,
    inactiveSubmitButtonState,
    onRowClick,
    onSetOrderSide,
    onSetOrderType,
    closePositionContent,
    maxOrderSizeBeforeOBProtectionTriggered,
  } = sectionProps;

  return (
    <ContentWrapper ref={modalRef} isMobile={mobileMode} style={{ bottom: 0 }}>
      <TradeForm onSubmit={handleSubmit(submitOrder)}>
        {showCloseModal && (
          <ClosePositionModal
            position={currentPosition}
            onHide={() => onClosePositionModalHide()}
            show={showCloseModal}
          />
        )}
        <FormContent>
          <Padding>
            <OptionFilter />
            <TransactionTypeWrapper>
              <SegmentedControl
                segments={buySellSegmentProps.segments}
                value={String(orderSide)}
                onSelect={onSetOrderSide}
                config={buySellSegmentProps.config}
              />
            </TransactionTypeWrapper>

            <OrderTypeWrapper>
              <SegmentedControl
                segments={limitMarketSegmentProps.segments}
                value={String(orderType)}
                onSelect={onSetOrderType}
                config={limitMarketSegmentProps.config}
              />
            </OrderTypeWrapper>
          </Padding>
          <DetailsWrapper>
            <InputsSection {...sectionProps} />
            <InfoSection {...sectionProps} />
          </DetailsWrapper>
        </FormContent>
        <SubmitWrapper isSticky={isSticky}>
          {accountSigningKeyState === AccountStateEnum.OK && !showOnboarding ? (
            <PlaceOrderButton
              disabled={
                Object.values(errors).length > 0 ||
                !amount ||
                !selectedInstrument ||
                accountData?.in_liquidation ||
                Number(amount) > maxOrderSizeBeforeOBProtectionTriggered ||
                isLoading ||
                Boolean(
                  !reduceOnlyDisabled && reduceOnlyNotAllowed && reduceOnly
                )
              }
              type={"submit"}
              side={orderSide}
            >
              {!isLoading ? (
                optionsTradeFormTranslations("place_order", {
                  side: commonFormTranslations(orderSide.toLowerCase()),
                })
              ) : (
                <Spinner color={sideColors(orderSide).on} />
              )}
            </PlaceOrderButton>
          ) : (
            inactiveSubmitButtonState
          )}
          {closePositionContent}
          <TradeExecutedBar
            type={orderSide}
            animate={animControls}
            initial={{
              opacity: 0,
              scale: 0,
            }}
          />
        </SubmitWrapper>
        <TicketDetails
          indexPriceCollar={INDEX_PRICE_COLLAR_OPTIONS}
          asset={market.asset}
          selectedInstrument={selectedInstrument}
          orderDirection={orderSide}
          averagePremium={
            Number.isNaN(totalValue / Number(amount))
              ? 0
              : totalValue / Number(amount)
          }
          totalSize={Number(amount)}
          showPlaceholder={!selectedInstrument}
          orderData={orderData}
          orderbookData={orderbookData}
          ticker={ticker}
          onOrderbookRowClick={onRowClick}
          mobileMode={mobileMode}
          indexPrice={Number(index?.price || 0)}
        />
      </TradeForm>
    </ContentWrapper>
  );
}

export default OptionsTradeForm;
